import { useState } from "react";

const CustomSoftwareSuccessMetricsTracker = () => {
  const [metrics, setMetrics] = useState({
    time: { before: 40, current: 25, target: 20 },
    manual: { before: 100, current: 40, target: 20 },
    adoption: { before: 0, current: 75, target: 90 },
    satisfaction: { before: 5, current: 7.5, target: 8.5 },
    cost: { before: 0, current: 50000, target: 75000 },
    revenue: { before: 0, current: 12, target: 20 },
    uptime: { before: 90, current: 99.2, target: 99.5 },
    error: { before: 50, current: 8, target: 5 },
  });

  const [results, setResults] = useState({
    showResults: false,
    overallScore: 0,
    healthClass: "",
    healthText: "",
    metricDetails: [],
    insights: [],
    recommendations: [],
  });

  const metricConfig = [
    {
      id: "time",
      name: "Time Saved",
      unit: "hrs/week",
      category: "Productivity",
      reverse: true,
      icon: "⏱️",
      description: "Hours saved per week using the software",
    },
    {
      id: "manual",
      name: "Manual Tasks",
      unit: "%",
      category: "Productivity",
      reverse: true,
      icon: "🤖",
      description: "Percentage of manual tasks eliminated",
    },
    {
      id: "adoption",
      name: "User Adoption",
      unit: "%",
      category: "Adoption",
      reverse: false,
      icon: "👥",
      description: "Percentage of users actively using the software",
    },
    {
      id: "satisfaction",
      name: "User Satisfaction",
      unit: "/10",
      category: "Adoption",
      reverse: false,
      icon: "😊",
      description: "User satisfaction score out of 10",
    },
    {
      id: "cost",
      name: "Cost Savings",
      unit: "₹/month",
      category: "Financial",
      reverse: false,
      icon: "💰",
      description: "Monthly cost savings in rupees",
    },
    {
      id: "revenue",
      name: "Revenue Increase",
      unit: "%",
      category: "Financial",
      reverse: false,
      icon: "📈",
      description: "Percentage increase in revenue",
    },
    {
      id: "uptime",
      name: "System Uptime",
      unit: "%",
      category: "Performance",
      reverse: false,
      icon: "⚡",
      description: "System availability percentage",
    },
    {
      id: "error",
      name: "Error Rate",
      unit: "per 1K",
      category: "Performance",
      reverse: true,
      icon: "🐛",
      description: "Errors per 1000 transactions",
    },
  ];

  const formatCurrency = (amount) => {
    return "₹" + amount.toLocaleString("en-IN");
  };

  const calculateProgress = (metric, current, target, before, reverse) => {
    if (reverse) {
      const improvement = before - current;
      const targetImprovement = before - target;
      return targetImprovement > 0
        ? (improvement / targetImprovement) * 100
        : 0;
    } else {
      const improvement = current - before;
      const targetImprovement = target - before;
      return targetImprovement > 0
        ? (improvement / targetImprovement) * 100
        : 0;
    }
  };

  const getStatus = (progress) => {
    if (progress >= 100)
      return {
        status: "achieved",
        text: "✅ Target Achieved",
        class: "success",
      };
    if (progress >= 75)
      return { status: "on-track", text: "🎯 On Track", class: "primary" };
    if (progress >= 50)
      return { status: "behind", text: "⚠️ Behind Schedule", class: "warning" };
    return { status: "critical", text: "🚨 Needs Attention", class: "danger" };
  };

  const calculateMetrics = () => {
    let totalScore = 0;
    let achievedCount = 0;
    let onTrackCount = 0;
    let behindCount = 0;
    let criticalCount = 0;

    const metricDetails = [];
    const categoryScores = {
      Productivity: [],
      Adoption: [],
      Financial: [],
      Performance: [],
    };

    metricConfig.forEach((config) => {
      const metric = metrics[config.id];
      const progress = calculateProgress(
        config.id,
        metric.current,
        metric.target,
        metric.before,
        config.reverse,
      );

      const cappedProgress = Math.min(Math.max(progress, 0), 150);
      const status = getStatus(cappedProgress);
      const score = Math.min(cappedProgress, 100);

      totalScore += score;

      if (status.status === "achieved") achievedCount++;
      else if (status.status === "on-track") onTrackCount++;
      else if (status.status === "behind") behindCount++;
      else criticalCount++;

      categoryScores[config.category].push(score);

      metricDetails.push({
        ...config,
        before: metric.before,
        current: metric.current,
        target: metric.target,
        progress: Math.round(cappedProgress),
        status: status.status,
        statusText: status.text,
        statusClass: status.class,
        score: Math.round(score),
      });
    });

    const averageScore = Math.round(totalScore / metricConfig.length);

    let healthClass = "";
    let healthText = "";
    let healthIcon = "";

    if (averageScore >= 80) {
      healthClass = "success";
      healthText = "🎉 Excellent! Project exceeding expectations";
      healthIcon = "🌟";
    } else if (averageScore >= 60) {
      healthClass = "warning";
      healthText = "👍 Good progress, some areas need attention";
      healthIcon = "📊";
    } else {
      healthClass = "danger";
      healthText = "⚠️ Action needed to improve outcomes";
      healthIcon = "🚨";
    }

    const insights = [];
    const recommendations = [];

    // Generate insights
    insights.push({
      title: "📈 Performance Summary",
      content: `Out of ${metricConfig.length} tracked metrics: ${achievedCount} achieved, ${onTrackCount} on track, ${behindCount} behind schedule, ${criticalCount} need immediate attention.`,
    });

    Object.entries(categoryScores).forEach(([category, scores]) => {
      if (scores.length > 0) {
        const avg = Math.round(
          scores.reduce((a, b) => a + b, 0) / scores.length,
        );
        let statusIcon =
          avg >= 80 ? "✅" : avg >= 60 ? "👍" : avg >= 40 ? "⚠️" : "🚨";
        insights.push({
          title: `${statusIcon} ${category} Performance`,
          content: `Average achievement: ${avg}% across ${scores.length} metrics`,
        });
      }
    });

    // Generate recommendations
    // recommendations.push('🎯 Recommended Actions');

    // if (criticalCount > 0) {
    //   recommendations.push('🔴 Priority: Focus immediately on critical metrics. Schedule review meetings with stakeholders.');
    // }

    // if (behindCount > 0) {
    //   recommendations.push('🟡 Identify blockers for behind-schedule metrics. Consider adjusting targets or allocating more resources.');
    // }

    // if (achievedCount >= metricConfig.length / 2) {
    //   recommendations.push('🟢 Document what worked well for achieved metrics. Apply those learnings to other areas.');
    // }

    // if (averageScore >= 80) {
    //   recommendations.push('🚀 Consider setting more ambitious targets for continuous improvement.');
    // }

    recommendations.push({
      icon: "🎯",
      text: "Recommended Actions",
    });

    if (criticalCount > 0) {
      recommendations.push({
        icon: "🔴",
        text: "Priority: Focus immediately on critical metrics. Schedule review meetings with stakeholders.",
      });
    }

    if (behindCount > 0) {
      recommendations.push({
        icon: "🟡",
        text: "Identify blockers for behind-schedule metrics. Consider adjusting targets or allocating more resources.",
      });
    }

    if (achievedCount >= metricConfig.length / 2) {
      recommendations.push({
        icon: "🟢",
        text: "Document what worked well for achieved metrics. Apply those learnings to other areas.",
      });
    }

    if (averageScore >= 80) {
      recommendations.push({
        icon: "🚀",
        text: "Consider setting more ambitious targets for continuous improvement.",
      });
    }

    setResults({
      showResults: true,
      overallScore: averageScore,
      healthClass,
      healthText,
      healthIcon,
      metricDetails,
      insights,
      recommendations,
    });

    setTimeout(() => {
      const resultsElement = document.getElementById("metrics-results");
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const handleInputChange = (metricId, field, value) => {
    setMetrics((prev) => ({
      ...prev,
      [metricId]: {
        ...prev[metricId],
        [field]: parseFloat(value) || 0,
      },
    }));
  };

  const resetMetrics = () => {
    setMetrics({
      time: { before: 40, current: 25, target: 20 },
      manual: { before: 100, current: 40, target: 20 },
      adoption: { before: 0, current: 75, target: 90 },
      satisfaction: { before: 5, current: 7.5, target: 8.5 },
      cost: { before: 0, current: 50000, target: 75000 },
      revenue: { before: 0, current: 12, target: 20 },
      uptime: { before: 90, current: 99.2, target: 99.5 },
      error: { before: 50, current: 8, target: 5 },
    });
    setResults({
      showResults: false,
      overallScore: 0,
      healthClass: "",
      healthText: "",
      metricDetails: [],
      insights: [],
      recommendations: [],
    });
  };

  return (
    <div className="metrics-tracker-page">
      {/* Hero Section */}
      <section
        style={{
          minHeight: "85vh",
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%)",
        }}
      >
        <div
          className="container-fluid"
          style={{
            backgroundPosition: "right center",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="container">
            <div className="row align-items-center min-vh-80">
              <div className="col-lg-7 col-md-12">
                <div className="hero-content text-white">
                  <div
                    className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-4"
                    style={{
                      background: "rgba(255, 255, 255, 0.1)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    <span style={{ fontSize: "20px" }}>📊</span>
                    <span
                      className="fw-bold"
                      style={{ fontSize: "14px", color: "#FFD700" }}
                    >
                      SUCCESS METRICS TRACKER
                    </span>
                  </div>

                  <h1
                    className="display-3 fw-bold mb-4"
                    style={{
                      lineHeight: "1.1",
                      background:
                        "linear-gradient(to right, #FFFFFF 0%, #E6F0FF 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      textShadow: "0 2px 10px rgba(0,0,0,0.1)",
                    }}
                  >
                    Custom Software{" "}
                    <span
                      className="d-inline-block"
                      style={{
                        background:
                          "linear-gradient(90deg, #FFD700 0%, #FFEC8B 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        position: "relative",
                        padding: "0 5px",
                      }}
                    >
                      Success Metrics
                    </span>{" "}
                    Tracker
                  </h1>

                  <p
                    className="fs-5 mb-5"
                    style={{
                      color: "#E6F0FF",
                      lineHeight: "1.6",
                      maxWidth: "90%",
                      fontWeight: "300",
                    }}
                  >
                    Track and measure your custom software project's success
                    with actionable metrics. Monitor ROI, adoption rates, and
                    performance improvements in real-time.
                  </p>

                  <div className="d-flex flex-wrap gap-3 mb-5">
                    <button
                      onClick={calculateMetrics}
                      className="btn btn-lg px-5 py-3 d-flex align-items-center gap-2"
                      style={{
                        background:
                          "linear-gradient(135deg, #FFD700 0%, #FFC107 100%)",
                        border: "none",
                        color: "#0a2458",
                        fontWeight: "700",
                        borderRadius: "12px",
                        transition: "all 0.3s ease",
                        boxShadow: "0 10px 30px rgba(255, 215, 0, 0.3)",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = "translateY(-3px)";
                        e.target.style.boxShadow =
                          "0 15px 40px rgba(255, 215, 0, 0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow =
                          "0 10px 30px rgba(255, 215, 0, 0.3)";
                      }}
                    >
                      <span>📊</span>
                      <span>Calculate Success Score</span>
                    </button>
                  </div>

                  <div className="d-flex align-items-center gap-4">
                    <div className="d-flex">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            background: "#1a3b8a",
                            border: "2px solid #0a2458",
                            marginLeft: i > 1 ? "-10px" : "0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontWeight: "600",
                          }}
                        >
                          {i === 4 ? "+" : i}
                        </div>
                      ))}
                    </div>
                    <p className="mb-0" style={{ color: "#E6F0FF" }}>
                      <strong>Trusted by 500+ businesses</strong> to track their
                      software success
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-lg-5 col-md-12">
                <div
                  style={{
                    position: "relative",
                    padding: "20px",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "-20px",
                      right: "-20px",
                      bottom: "-20px",
                      left: "-20px",
                      background:
                        "linear-gradient(135deg, rgba(255,215,0,0.2) 0%, rgba(255,215,0,0) 50%)",
                      borderRadius: "25px",
                      zIndex: 0,
                    }}
                  />
                  <div
                    style={{
                      position: "relative",
                      borderRadius: "20px",
                      overflow: "hidden",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                      position: "relative",
                      zIndex: 1,
                      background: "rgba(255, 255, 255, 0.1)",
                      backdropFilter: "blur(10px)",
                      padding: "30px",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "64px", marginBottom: "20px" }}>
                      📈
                    </div>
                    <h3 style={{ color: "white", marginBottom: "15px" }}>
                      Track Key Metrics
                    </h3>
                    <div className="row">
                      <div className="col-6">
                        <div
                          style={{
                            color: "#FFD700",
                            fontSize: "32px",
                            fontWeight: "bold",
                          }}
                        >
                          8+
                        </div>
                        <div
                          style={{
                            color: "rgba(255,255,255,0.8)",
                            fontSize: "14px",
                          }}
                        >
                          Metrics Tracked
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          style={{
                            color: "#FFD700",
                            fontSize: "32px",
                            fontWeight: "bold",
                          }}
                        >
                          4
                        </div>
                        <div
                          style={{
                            color: "rgba(255,255,255,0.8)",
                            fontSize: "14px",
                          }}
                        >
                          Categories
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container main-content" style={{ padding: "80px 20px" }}>
        {/* Info Box */}
        <div
          className="mb-5 p-4 rounded-4"
          style={{
            background:
              "linear-gradient(135deg, rgba(10, 36, 88, 0.05) 0%, rgba(255, 215, 0, 0.05) 100%)",
            border: "1px solid rgba(10, 36, 88, 0.1)",
          }}
        >
          <div className="d-flex align-items-center gap-3 mb-3">
            <div style={{ fontSize: "32px" }}>📈</div>
            <h3 style={{ color: "#0a2458", margin: 0 }}>Why Track Metrics?</h3>
          </div>
          <p style={{ color: "#0A2458", lineHeight: "1.6", margin: 0 }}>
            Success isn't just about launching software—it's about achieving
            measurable business outcomes. This tracker helps you monitor key
            performance indicators (KPIs) and ensure your custom software
            delivers the expected ROI.
          </p>
        </div>

        {/* Metrics Setup */}
        <div className="metrics-setup mb-5">
          <div className="text-center mb-5">
            <h2
              style={{
                color: "#0a2458",
                fontSize: "2rem",
                marginBottom: "10px",
              }}
            >
              Define Your Success Metrics
            </h2>
            <p style={{ color: "#0A2458" }}>
              Enter your baseline, current, and target values for each metric
            </p>
          </div>

          {metricConfig.map((config, index) => (
            <div key={config.id} className="metric-category mb-4">
              <div className="d-flex align-items-center gap-3 mb-3">
                <div style={{ fontSize: "24px" }}>{config.icon}</div>
                <div>
                  <h4 style={{ color: "#0a2458", margin: 0 }}>{config.name}</h4>
                  <p style={{ color: "#0A2458", fontSize: "14px", margin: 0 }}>
                    {config.description}
                  </p>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-4">
                  <div className="input-card">
                    <label className="input-label">Baseline (Before)</label>
                    <div className="input-with-unit">
                      <input
                        type="number"
                        value={metrics[config.id].before}
                        onChange={(e) =>
                          handleInputChange(config.id, "before", e.target.value)
                        }
                        className="form-input"
                        placeholder="Enter baseline"
                      />
                      <span className="input-unit">{config.unit}</span>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="input-card">
                    <label className="input-label">Current Value</label>
                    <div className="input-with-unit">
                      <input
                        type="number"
                        value={metrics[config.id].current}
                        onChange={(e) =>
                          handleInputChange(
                            config.id,
                            "current",
                            e.target.value,
                          )
                        }
                        className="form-input"
                        placeholder="Enter current"
                      />
                      <span className="input-unit">{config.unit}</span>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="input-card">
                    <label className="input-label">Target Goal</label>
                    <div className="input-with-unit">
                      <input
                        type="number"
                        value={metrics[config.id].target}
                        onChange={(e) =>
                          handleInputChange(config.id, "target", e.target.value)
                        }
                        className="form-input"
                        placeholder="Enter target"
                      />
                      <span className="input-unit">{config.unit}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="text-center mt-5">
            <button onClick={calculateMetrics} className="calculate-btn">
              <span className="btn-icon">📊</span>
              <span className="btn-text">Calculate Success Score</span>
              <span className="btn-arrow">→</span>
            </button>
            <p className="mt-3" style={{ color: "#0A2458", fontSize: "14px" }}>
              Based on your current progress towards defined targets
            </p>
          </div>
        </div>

        {/* Results Section */}
        {results.showResults && (
          <div className="results-section" id="metrics-results">
            {/* Overall Health */}
            <div className={`overall-health ${results.healthClass}`}>
              <div className="health-content">
                <div className="health-icon">{results.healthIcon}</div>
                <div className="health-text">
                  <h3>Overall Project Health</h3>
                  <p>{results.healthText}</p>
                </div>
                <div className="health-score">{results.overallScore}%</div>
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="metrics-grid">
              {results.metricDetails.map((metric, index) => (
                <div key={index} className="metric-card">
                  <div className="metric-header">
                    <div className="metric-icon">{metric.icon}</div>
                    <div>
                      <h4>{metric.name}</h4>
                      <p className="metric-category">{metric.category}</p>
                    </div>
                  </div>

                  <div className="metric-values">
                    <div className="value-group">
                      <span className="value-label">Baseline</span>
                      <span className="value">
                        {metric.before}
                        {metric.unit}
                      </span>
                    </div>
                    <div className="value-group">
                      <span className="value-label">Current</span>
                      <span className="value current">
                        {metric.current}
                        {metric.unit}
                      </span>
                    </div>
                    <div className="value-group">
                      <span className="value-label">Target</span>
                      <span className="value target">
                        {metric.target}
                        {metric.unit}
                      </span>
                    </div>
                  </div>

                  <div className="progress-section">
                    <div className="progress-bar">
                      <div
                        className={`progress-fill ${metric.statusClass}`}
                        style={{ width: `${Math.min(metric.progress, 100)}%` }}
                      ></div>
                    </div>
                    <div className="progress-info">
                      <span className="progress-percent">
                        {metric.progress}%
                      </span>
                      <span className={`status-badge ${metric.statusClass}`}>
                        {metric.statusText}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Insights & Recommendations */}
            <div className="insights-section">
              <div className="insights-card">
                <div className="section-header">
                  <div className="section-icon">💡</div>
                  <h3>Insights & Analysis</h3>
                </div>

                <div className="insights-grid">
                  {results.insights.map((insight, index) => (
                    <div key={index} className="insight-item">
                      <h4>{insight.title}</h4>
                      <p style={{ fontSize: 16 }}>{insight.content}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="recommendations-card">
                <div className="section-header">
                  <div className="section-icon">🎯</div>
                  <h3>Recommended Actions</h3>
                </div>

                <div className="recommendations-list">
                  {results.recommendations.map((rec, index) => (
                    <div key={index} className="recommendation-item">
                      {/* <div className="rec-icon">{rec.charAt(0)}</div>
                      <div className="rec-content">{rec.substring(1)}</div> */}

                      <div className="rec-icon">{rec.icon}</div>
                      <div className="rec-content">{rec.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Reset Button */}
            <div className="text-center mt-5">
              <button onClick={resetMetrics} className="reset-btn">
                <span>↻</span>
                <span>Reset & Try Different Values</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .metrics-tracker-page {
          font-family:
            -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          overflow-x: hidden;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          width: 100%;
          box-sizing: border-box;
        }

        .min-vh-80 {
          min-height: 80vh;
        }

        /* Main Content */
        .main-content {
          background: #ffffff;
        }

        /* Input Cards */
        .input-card {
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px;
          transition: all 0.3s ease;
        }

        .input-card:hover {
          border-color: #0a2458;
          background: #f0f7ff;
        }

        .input-label {
          display: block;
          color: #0a2458;
          font-weight: 600;
          margin-bottom: 10px;
          font-size: 14px;
        }

        .input-with-unit {
          position: relative;
        }

        .form-input {
          width: 100%;
          padding: 12px 40px 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 16px;
          transition: all 0.3s ease;
          background: white;
        }

        .form-input:focus {
          outline: none;
          border-color: #0a2458;
          box-shadow: 0 0 0 3px rgba(10, 36, 88, 0.1);
        }

        .input-unit {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #0a2458;
          font-size: 14px;
          font-weight: 600;
        }

        /* Calculate Button */
        .calculate-btn {
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          color: white;
          border: none;
          padding: 20px 40px;
          font-size: 18px;
          font-weight: 600;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .calculate-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 15px 30px rgba(10, 36, 88, 0.2);
        }

        /* Results Section */
        .results-section {
          animation: slideIn 0.6s ease;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Overall Health */
        .overall-health {
          background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
          color: white;
          border-radius: 20px;
          padding: 40px;
          margin-bottom: 40px;
        }

        .overall-health.success {
          background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
        }

        .overall-health.warning {
          background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
        }

        .overall-health.danger {
          background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
        }

        .health-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
        }

        .health-icon {
          font-size: 48px;
          flex-shrink: 0;
        }

        .health-text {
          flex: 1;
        }

        .health-text h3 {
          font-size: 24px;
          margin-bottom: 8px;
        }

        .health-text p {
          font-size: 16px;
          opacity: 0.9;
          margin: 0;
        }

        .health-score {
          font-size: 64px;
          font-weight: 700;
          flex-shrink: 0;
        }

        /* Metrics Grid */
        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 24px;
          margin-bottom: 40px;
        }

        @media (max-width: 768px) {
          .metrics-grid {
            grid-template-columns: 1fr;
          }
        }

        .metric-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 24px;
          transition: all 0.3s ease;
        }

        .metric-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        .metric-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
        }

        .metric-icon {
          font-size: 32px;
          width: 60px;
          height: 60px;
          background: rgba(10, 36, 88, 0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .metric-header h4 {
          color: #0a2458;
          margin: 0 0 4px 0;
          font-size: 18px;
        }

        .metric-category {
          color: #0a2458;
          font-size: 14px;
          margin: 0;
        }

        .metric-values {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }

        .value-group {
          text-align: center;
        }

        .value-label {
          display: block;
          color: #0a2458;
          font-size: 12px;
          margin-bottom: 4px;
        }

        .value {
          display: block;
          font-weight: 600;
          color: #0a2458;
          font-size: 14px;
        }

        .value.current {
          color: #0a2458;
          font-size: 16px;
        }

        .value.target {
          color: #10b981;
        }

        .progress-section {
          margin-top: 20px;
        }

        .progress-bar {
          width: 100%;
          height: 8px;
          background: #e2e8f0;
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 8px;
        }

        .progress-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.5s ease;
        }

        .progress-fill.success {
          background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
        }

        .progress-fill.primary {
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
        }

        .progress-fill.warning {
          background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
        }

        .progress-fill.danger {
          background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
        }

        .progress-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .progress-percent {
          font-weight: 600;
          color: #0a2458;
          font-size: 14px;
        }

        .status-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .status-badge.success {
          background: rgba(16, 185, 129, 0.1);
          color: #065f46;
        }

        .status-badge.primary {
          background: rgba(10, 36, 88, 0.1);
          color: #0a2458;
        }

        .status-badge.warning {
          background: rgba(245, 158, 11, 0.1);
          color: #92400e;
        }

        .status-badge.danger {
          background: rgba(220, 38, 38, 0.1);
          color: #991b1b;
        }

        /* Insights Section */
        .insights-section {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 30px;
          margin-bottom: 40px;
        }

        @media (max-width: 992px) {
          .insights-section {
            grid-template-columns: 1fr;
          }
        }

        .insights-card,
        .recommendations-card {
          background: white;
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
        }

        .section-icon {
          font-size: 28px;
        }

        .section-header h3 {
          color: #0a2458;
          margin: 0;
        }

        .insights-grid {
          display: grid;
          gap: 16px;
        }

        .insight-item {
          padding: 20px;
          background: #f8fafc;
          border-radius: 12px;
        }

        .insight-item h4 {
          color: #0a2458;
          margin: 0 0 8px 0;
          font-size: 16px;
        }

        .insight-item p {
          color: #0a2458;
          margin: 0;
          font-size: 14px;
          line-height: 1.6;
        }

        .recommendations-list {
          display: grid;
          gap: 12px;
        }

        .recommendation-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px;
          background: #fffbeb;
          border-radius: 8px;
        }

        .rec-icon {
          font-size: 18px;
          flex-shrink: 0;
        }

        .rec-content {
          color: #92400e;
          font-size: 14px;
          line-height: 1.5;
        }

        /* Reset Button */
        .reset-btn {
          background: transparent;
          color: #0a2458;
          border: 2px solid #0a2458;
          padding: 12px 32px;
          border-radius: 8px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .reset-btn:hover {
          background: rgba(10, 36, 88, 0.05);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .health-content {
            flex-direction: column;
            text-align: center;
            gap: 16px;
          }

          .health-score {
            font-size: 48px;
          }

          .metric-values {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .input-card {
            padding: 16px;
          }

          .calculate-btn {
            width: 100%;
            justify-content: center;
            padding: 16px 24px;
          }
        }

        @media (max-width: 480px) {
          .container {
            padding: 0 15px;
          }

          .main-content {
            padding: 40px 15px;
          }

          .overall-health {
            padding: 30px 20px;
          }

          .insights-card,
          .recommendations-card {
            padding: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default CustomSoftwareSuccessMetricsTracker;
